/*
 * GG — Product gallery full-screen zoom
 *
 * Clicking any photo of the product gallery opens a full-screen <dialog> with the
 * photos in a swipeable track and a thumbnail rail (left on desktop, bottom on mobile).
 *
 * Design notes
 * - One delegated listener on the document: the theme re-renders the gallery when the
 *   customer switches variant, so listeners attached to the old nodes would die.
 * - The dialog is built from the gallery DOM at open time (always matches what the
 *   customer currently sees: variant order, hidden variant photos, videos skipped).
 * - Only the main product gallery (.gg-pdp-outer) is handled, not quick-add modals.
 */
(() => {
  if (window.__ggGalleryZoom) return;
  window.__ggGalleryZoom = true;

  const ITEM_SELECTOR = '.gg-pdp-outer .product__media-list .product__media-item';
  const BIG_WIDTH = 2000;
  const THUMB_WIDTH = 200;
  let dialog = null;

  const sized = (src, width) => {
    try {
      const url = new URL(src, window.location.href);
      url.searchParams.set('width', width);
      return url.toString();
    } catch (e) {
      return src;
    }
  };

  const imageItems = () =>
    Array.from(document.querySelectorAll(ITEM_SELECTOR)).filter(
      (li) => li.querySelector('.media-type-image') && li.getClientRects().length > 0
    );

  const closeIcon =
    '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true"><path d="M5 5l14 14M19 5L5 19"/></svg>';

  const build = () => {
    const dlg = document.createElement('dialog');
    dlg.className = 'gg-zoom';
    dlg.setAttribute('aria-label', document.title);
    dlg.innerHTML =
      '<div class="gg-zoom__top">' +
      '<button type="button" class="gg-zoom__close" data-gg-zoom-close aria-label="Close">' +
      closeIcon +
      '</button></div>' +
      '<div class="gg-zoom__body">' +
      '<div class="gg-zoom__thumbs" data-gg-zoom-thumbs></div>' +
      '<div class="gg-zoom__track" data-gg-zoom-track tabindex="0"></div>' +
      '</div>';
    document.body.appendChild(dlg);

    const track = dlg.querySelector('[data-gg-zoom-track]');
    let raf = null;
    track.addEventListener('scroll', () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        setActive(dlg, Math.round(track.scrollLeft / (track.clientWidth || 1)));
      });
    });

    dlg.addEventListener('click', (event) => {
      if (event.target.closest('[data-gg-zoom-close]')) {
        dlg.close();
        return;
      }
      const thumb = event.target.closest('[data-gg-zoom-go]');
      if (thumb) goTo(dlg, Number(thumb.dataset.ggZoomGo), true);
    });

    dlg.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') goTo(dlg, currentIndex(dlg) + 1, true);
      if (event.key === 'ArrowLeft') goTo(dlg, currentIndex(dlg) - 1, true);
    });

    dlg.addEventListener('close', () => {
      document.documentElement.classList.remove('gg-zoom-open');
      if (dlg.__opener && document.contains(dlg.__opener)) dlg.__opener.focus({ preventScroll: true });
    });

    return dlg;
  };

  const currentIndex = (dlg) => {
    const track = dlg.querySelector('[data-gg-zoom-track]');
    return Math.round(track.scrollLeft / (track.clientWidth || 1));
  };

  const setActive = (dlg, index) => {
    dlg.querySelectorAll('[data-gg-zoom-go]').forEach((thumb) => {
      const active = Number(thumb.dataset.ggZoomGo) === index;
      thumb.classList.toggle('is-active', active);
      if (active) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
      if (active) thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    });
  };

  const goTo = (dlg, index, smooth) => {
    const track = dlg.querySelector('[data-gg-zoom-track]');
    const last = track.children.length - 1;
    const target = Math.max(0, Math.min(index, last));
    track.scrollTo({ left: target * track.clientWidth, behavior: smooth ? 'smooth' : 'instant' });
    setActive(dlg, target);
  };

  const open = (index, opener) => {
    const items = imageItems();
    if (!items.length) return;
    if (!dialog) dialog = build();

    const track = dialog.querySelector('[data-gg-zoom-track]');
    const thumbs = dialog.querySelector('[data-gg-zoom-thumbs]');
    track.innerHTML = '';
    thumbs.innerHTML = '';

    items.forEach((li, i) => {
      const img = li.querySelector('img');
      const src = img.currentSrc || img.src;
      const alt = img.alt || document.title;

      const slide = document.createElement('div');
      slide.className = 'gg-zoom__slide';
      const big = document.createElement('img');
      big.src = sized(src, BIG_WIDTH);
      big.alt = alt;
      big.decoding = 'async';
      big.loading = Math.abs(i - index) <= 1 ? 'eager' : 'lazy';
      slide.appendChild(big);
      track.appendChild(slide);

      const thumb = document.createElement('button');
      thumb.type = 'button';
      thumb.className = 'gg-zoom__thumb';
      thumb.dataset.ggZoomGo = String(i);
      thumb.setAttribute('aria-label', String(i + 1));
      const thumbImg = document.createElement('img');
      thumbImg.src = sized(src, THUMB_WIDTH);
      thumbImg.alt = '';
      thumbImg.loading = 'lazy';
      thumb.appendChild(thumbImg);
      thumbs.appendChild(thumb);
    });

    dialog.classList.toggle('gg-zoom--single', items.length < 2);
    dialog.__opener = opener || null;
    document.documentElement.classList.add('gg-zoom-open');
    dialog.showModal();
    goTo(dialog, index, false);
    track.focus({ preventScroll: true });
  };

  // Capture phase: runs before the theme's own modal-opener, which we replace.
  document.addEventListener(
    'click',
    (event) => {
      const li = event.target.closest && event.target.closest(ITEM_SELECTOR);
      if (!li || !li.querySelector('.media-type-image')) return;
      // Videos keep their own behaviour
      if (event.target.closest('deferred-media video, .deferred-media__poster-button')) return;
      const items = imageItems();
      const index = items.indexOf(li);
      if (index < 0) return;
      event.preventDefault();
      event.stopPropagation();
      open(index, li.querySelector('.product__media-toggle') || null);
    },
    true
  );
})();
